import { useMemo } from 'react';

import { ContainerFlow, type YardMovesResponse } from '@/api/generated.schemas';
import {
  DEFAULT_TIMEZONE,
  FLOW_KEYS,
  formatShiftDayTick,
} from '@/routes/(app)/(scenarios)/(simulation)/yard-moves.utils';
import {
  buildYardMovesPanels,
  type VesselNameById,
  type YardMovesChartFilters,
} from '@/routes/(app)/(scenarios)/(simulation)/yard-moves-chart.utils';

export function useYardMovesChart(
  data: YardMovesResponse | undefined,
  filters: YardMovesChartFilters
) {
  const timezone = data?.timezone ?? DEFAULT_TIMEZONE;
  const shifts = data?.shifts;
  const shipVisits = data?.ship_visits;

  const vesselNameById = useMemo<VesselNameById>(
    () =>
      Object.fromEntries(
        (shipVisits ?? []).map((visit) => [visit.id, visit.vessel_name])
      ),
    [shipVisits]
  );
  const visiblePanels = useMemo(
    () => (data ? buildYardMovesPanels(data, filters) : []),
    [data, filters]
  );
  const hasVisibleMoves = visiblePanels.some(
    (panel) => panel.visitIds.length > 0
  );

  /** x values of the first shift of each day (the only labelled ticks). */
  const dayTicks = useMemo(
    () =>
      (shifts ?? [])
        .map((shift) => shift.start_time)
        .filter((iso) => formatShiftDayTick(iso, timezone) !== ''),
    [shifts, timezone]
  );
  const shiftEndById = useMemo<Record<string, string>>(
    () =>
      Object.fromEntries(
        (shifts ?? []).map((shift) => [shift.id, shift.end_time])
      ),
    [shifts]
  );
  const visitOptions = useMemo(
    () =>
      (shipVisits ?? []).map((visit) => ({
        id: visit.id,
        vesselName: visit.vessel_name,
      })),
    [shipVisits]
  );
  /** Translation key + active flag per flow, for the export header. */
  const exportFilters = useMemo(
    () => [
      ...Object.values(ContainerFlow).map((flow) => ({
        key: FLOW_KEYS[flow],
        active: filters.containerFlows.has(flow),
      })),
    ],
    [filters.containerFlows]
  );

  return {
    timezone,
    visiblePanels,
    hasVisibleMoves,
    dayTicks,
    shiftEndById,
    vesselNameById,
    visitOptions,
    exportFilters,
  };
}
